import React from 'react';
import styled, { css } from 'styled-components';
import Spinner from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  isLoading?: boolean;
}

const variantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'secondary':
      return css`
        background: ${(p) => p.theme.navy};
        color: #fff;
        &:hover:not(:disabled) { background: ${(p) => p.theme.navySoft}; }
      `;
    case 'outline':
      return css`
        background: transparent;
        color: ${(p) => p.theme.textPrimary};
        border: 1px solid ${(p) => p.theme.border};
        &:hover:not(:disabled) { border-color: ${(p) => p.theme.primary}; color: ${(p) => p.theme.primary}; }
      `;
    case 'ghost':
      return css`
        background: transparent;
        color: ${(p) => p.theme.textSecondary};
        &:hover:not(:disabled) { background: ${(p) => p.theme.lightGray}; }
      `;
    case 'danger':
      return css`
        background: ${(p) => p.theme.error};
        color: #fff;
        &:hover:not(:disabled) { filter: brightness(0.94); }
      `;
    case 'primary':
    default:
      return css`
        background: ${(p) => p.theme.primary};
        color: #fff;
        &:hover:not(:disabled) { background: ${(p) => p.theme.primaryDark}; }
      `;
  }
};

const StyledButton = styled.button<{ $variant: ButtonVariant; $fullWidth?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  line-height: 1.2;
  border: none;
  border-radius: ${(p) => p.theme.radius};
  cursor: pointer;
  transition: all 0.18s ease;
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
  white-space: nowrap;
  ${(p) => variantStyles(p.$variant)}

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  fullWidth,
  isLoading,
  disabled,
  ...rest
}) => (
  <StyledButton $variant={variant} $fullWidth={fullWidth} disabled={disabled || isLoading} {...rest}>
    {isLoading && <Spinner size={15} />}
    {children}
  </StyledButton>
);

export default Button;
